import { TOP_UP_STATES, type TopUpState } from "@kilat/contracts/top-up";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { ListPagination } from "@/components/list-pagination";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangePicker } from "@/features/billing/date-range-picker";
import { STAFF_TOP_UPS_PER_PAGE } from "@/features/staff/api";
import { staffTopUpsQuery } from "@/features/staff/queries";
import { StaffTopUpTable } from "@/features/staff/top-up-table";
import { STAFF_TOP_UP_STATE_LABELS } from "@/features/staff/top-up-states";
import { m } from "@/lib/paraglide/messages.js";

// The queue's filters are route state (guide, rule 16): a link to "this reseller's disputes in
// October" opens just that. Anything the API would refuse reads as not there, and a range given
// backwards is read the right way round. No range means every day: unlike the billing history,
// the queue has no default month, so a dispute from an earlier month is never hidden.
const TopUpsSearch = z.object({
  state: z.enum(TOP_UP_STATES).optional().catch(undefined),
  reseller: z
    .string()
    .regex(/^[0-9A-Za-z]{22}$/)
    .optional()
    .catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

function readTopUpsSearch(raw: Record<string, unknown>) {
  const search = TopUpsSearch.parse(raw);
  const { from, to } = search;
  return from && to && from > to ? { ...search, from: to, to: from } : search;
}

const ALL = "all";

// /staff/top-ups: every reseller's top-ups (docs/plans/billing-top-up.md §7), what finance must
// decide first: disputed and awaiting a second approval, then the rest, newest first. Any staff
// role reads it; Approve and Reject are offered on a dispute, and the API decides who may (T11).
export const Route = createFileRoute("/staff/_authenticated/top-ups")({
  validateSearch: (raw: Record<string, unknown>) => readTopUpsSearch(raw),
  loaderDeps: ({ search }) => ({
    state: search.state,
    reseller: search.reseller,
    from: search.from,
    to: search.to,
    page: search.page ?? 1,
  }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(staffTopUpsQuery(deps)),
  staticData: { crumb: m.staff_nav_top_ups },
  component: TopUpsQueue,
});

function TopUpsQueue() {
  const { state, reseller, from, to, page: asked = 1 } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: topUps } = useSuspenseQuery(staffTopUpsQuery({ state, reseller, from, to, page: asked }));
  const { page, total } = topUps.meta;
  const pages = Math.max(1, Math.ceil(total / STAFF_TOP_UPS_PER_PAGE));
  const shown = topUps.data.length;
  const first = shown === 0 ? 0 : (page - 1) * STAFF_TOP_UPS_PER_PAGE + 1;
  const range = m.common_page_range({ from: first, to: first + shown - (shown ? 1 : 0), total });
  const states = [
    { value: ALL, label: m.staff_top_ups_state_all() },
    ...TOP_UP_STATES.map((value) => ({ value, label: STAFF_TOP_UP_STATE_LABELS[value]() })),
  ];
  // A changed filter starts again from the first page; replaced, so Back leaves the page.
  const onStateChange = (next: string | null) => {
    if (!next) return;
    void navigate({
      search: (prev) => ({ ...prev, state: next === ALL ? undefined : (next as TopUpState), page: undefined }),
      replace: true,
    });
  };
  const onRangeChange = (next: { from?: string | undefined; to?: string | undefined }) =>
    void navigate({ search: (prev) => ({ ...prev, from: next.from, to: next.to, page: undefined }), replace: true });
  return (
    <>
      <Item>
        <ItemContent>
          <ItemTitle>{m.staff_top_ups_title()}</ItemTitle>
          <ItemDescription>{m.staff_top_ups_description()}</ItemDescription>
        </ItemContent>
      </Item>
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select items={states} value={state ?? ALL} onValueChange={onStateChange}>
              <SelectTrigger aria-label={m.staff_top_ups_state_filter()}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {states.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangePicker from={from} to={to} label={m.billing_date_range()} onChange={onRangeChange} />
            {(from || to) && (
              <Button variant="ghost" onClick={() => onRangeChange({})}>
                {m.staff_top_ups_all_dates()}
              </Button>
            )}
            {reseller && (
              <Button
                variant="link"
                render={
                  <Link
                    from="/staff/top-ups"
                    to="/staff/top-ups"
                    search={(prev) => ({ ...prev, reseller: undefined, page: undefined })}
                  />
                }
              >
                {m.staff_top_ups_clear_reseller()}
              </Button>
            )}
          </div>
          <StaffTopUpTable topUps={topUps.data} caption={range} />
          <ListPagination page={page} pages={pages} />
        </CardContent>
      </Card>
    </>
  );
}
